import React, { useEffect, useState, SyntheticEvent } from 'react'

import {
  TextInput,
  DropdownMenu,
  Button,
  ContextualAlert,
  TextArea,
  RadioButton,
} from '@paypalcorp/pp-react'

import type {
  MultipartyFormProps,
  CountriesType,
  FormDataType,
  FPTITypes,
} from './MultipartyForm.types'

import formContent from './multiparty-form.yaml'
import { getRequest } from '../../../utils/request'
import styles from './multiparty-form.module.css'
import withAnalytics from '../../../utils/withAnalytics'
import { downloadZipFile } from '../../../utils'
import GreenTick from '../../icons/greenTick'

const pardotFormurl = 'https://pypd.paypal-mktg.com/l/925803/2022-12-07/2qjkq'
const pardotFormName = 'NA-US-FRH-22-Partner-Self-Service-Not-Live'
const emailAddressRegex = /.+\@.+\..+/
const companyWebsiteRegex =
  /^((https?|ftp|smtp):\/\/)?(www.)?[a-z0-9]+\.[a-z]+(\/[a-zA-Z0-9#]+\/?)*$/

const MultipartyForm: React.FC<MultipartyFormProps> = ({
  fpti,
  auto = false,
}) => {
  const [formSubmitted, setFormSubmitted] = useState(false)
  const [countries, setCountries] = useState<CountriesType[]>([])
  const [cav_lt_10m, setCAVFlag] = useState(false)

  const fptiData: FPTITypes | boolean =
    typeof window !== `undefined` && window.fpti

  const [formData, setFormData] = useState<FormDataType>({
    firstName: '',
    lastName: '',
    emailAddress: '',
    company: '',
    companyWebsite: '',
    country: '',
    describeYourSolution: '',
    companyType: '',
    clientOnlineVolume: '',
    productsandServices: 'yes',
    cardProcessing: 'yes',
    currentlyProcessing: '',
    privacyConsent: false,
  })

  const [formErrors, setFormErrors] = useState({
    firstName: { isValid: false, isTouched: false },
    lastName: { isValid: false, isTouched: false },
    emailAddress: { isValid: false, isTouched: false },
    company: { isValid: false, isTouched: false },
    companyWebsite: { isValid: false, isTouched: false },
    country: { isValid: false, isTouched: false },
    describeYourSolution: { isValid: false, isTouched: false },
    companyType: { isValid: false, isTouched: false },
    clientOnlineVolume: { isValid: false, isTouched: false },
    privacyConsent: { isValid: false, isTouched: false },
    currentlyProcessing: { isValid: false, isTouched: false },
  })

  const lessThanTargetVl = '1000000'
  const greaterThanTargetVl = '10000000'

  const getCountries = async () => {
    try {
      const { data } = await getRequest('/apis/countries')
      setCountries(
        data.countries.map((c) => {
          return { value: c.countryCode, primaryText: c.countryName }
        }),
      )
    } catch (err) {
      const countries = [{ value: 'US', primaryText: 'United States' }]
      setCountries(countries)
    }
  }

  const onFormChange = (field: string, value: string | boolean) => {
    if (field === 'clientOnlineVolume') {
      localStorage.setItem('client-volume', value)
    }

    setFormData((prevFormData) => ({ ...prevFormData, [field]: value }))

    if (!value) {
      setFormErrors((prevFormErrors) => ({
        ...prevFormErrors,
        [field]: { isTouched: true, isValid: false },
      }))
    } else {
      // eslint-disable-next-line no-lonely-if
      if (
        (field === 'emailAddress' && !emailAddressRegex.test(value)) ||
        (field === 'companyWebsite' && !companyWebsiteRegex.test(value))
      ) {
        setFormErrors((prevFormErrors) => ({
          ...prevFormErrors,
          [field]: { isTouched: true, isValid: false },
        }))
      } else if (
        field === 'clientOnlineVolume' &&
        value === greaterThanTargetVl
      ) {
        setFormErrors((prevFormErrors) => ({
          ...prevFormErrors,
          clientOnlineVolume: { isTouched: true, isValid: true },
          currentlyProcessing: { isTouched: true, isValid: true },
        }))
      } else if (
        field === 'clientOnlineVolume' &&
        (value === lessThanTargetVl || value === '')
      ) {
        setFormErrors((prevFormErrors) => ({
          ...prevFormErrors,
          clientOnlineVolume: { isTouched: true, isValid: true },
          currentlyProcessing: { isTouched: true, isValid: false },
        }))
      } else {
        setFormErrors((prevFormErrors) => ({
          ...prevFormErrors,
          [field]: { isTouched: true, isValid: true },
        }))
      }
    }
  }

  const validate = (type: any) => {
    const errors = {
      ...formErrors,
      currentlyProcessing: {
        ...formErrors.currentlyProcessing,
        isValid:
          formData.cardProcessing === 'no'
            ? true
            : formErrors.currentlyProcessing.isValid,
      },
    }

    return Object.values(errors).reduce((cur, next) => {
      return (cur = cur && next[type])
    }, true)
  }

  const handleFormSubmit = (pdFormName: string, e: SyntheticEvent) => {
    e.preventDefault()

    // Validate Form
    const isFormValid = validate('isTouched') && validate('isValid')

    if (!isFormValid) {
      Object.keys(formData).forEach((field) => {
        if (
          field === 'currentlyProcessing' &&
          formData.clientOnlineVolume === greaterThanTargetVl
        ) {
          setFormErrors((prevFormErrors) => ({
            ...prevFormErrors,
            currentlyProcessing: { isTouched: true, isValid: true },
          }))
        } else if (
          (field === 'privacyConsent' && !formData[field]) ||
          !formData[field]
        ) {
          setFormErrors((prevFormErrors) => ({
            ...prevFormErrors,
            [field]: { isTouched: true, isValid: false },
          }))
        }
      })
    }

    if (isFormValid && fptiData) {
      const options = {
        data: {
          page: pardotFormName,
          pgrp: pardotFormName,
          e: 'cl',
          link: `Form submitted button clicked`,
          component: 'devdiscoverynodeweb',
        },
      }

      if (window.PAYPAL?.analytics?.instance) {
        window.PAYPAL?.analytics?.instance?.record(options)
      }

      const pardotFormEle = document.getElementById(
        pdFormName,
      ) as HTMLFormElement
      pardotFormEle.submit()
    }
  }

  const downloadZip = () => {
    downloadZipFile()

    if (fptiData) {
      const options = {
        data: {
          page: pardotFormName,
          pgrp: pardotFormName,
          e: 'cl',
          link: `docs_multiparty - download zip file`,
          component: 'devdiscoverynodeweb',
        },
      }

      if (window.PAYPAL?.analytics?.instance) {
        window.PAYPAL?.analytics?.instance?.record(options)
      }
    }
  }

  useEffect(() => {
    ;(async function () {
      await getCountries()
    })()

    const isFormSubmitted =
      typeof window !== `undefined` &&
      window.location.search !== '' &&
      window.location?.search.substring(1).split('=')[1]
    setFormSubmitted(!!isFormSubmitted)

    // Success Path
    if (!!isFormSubmitted && fptiData) {
      const options = {
        data: {
          page: pardotFormName,
          pgrp: pardotFormName,
          e: 'im',
          link: `Multiparty form successfully submitted`,
          component: 'devdiscoverynodeweb',
        },
      }

      if (window.PAYPAL?.analytics?.instance) {
        window.PAYPAL?.analytics?.instance?.record(options)
      }
    }

    const clientVolume = localStorage.getItem('client-volume')
    if (clientVolume && clientVolume === lessThanTargetVl) {
      setCAVFlag(true)
    } else {
      setCAVFlag(false)
    }
  }, [])

  return (
    <div className={styles.successCardmb2}>
      {formSubmitted && cav_lt_10m && (
        <div className={styles.block}>
          <h2>We recommend</h2>
          <p className={styles.successMessage}>
            <span className={styles.greenTickSpace}>
              <GreenTick />
            </span>
            A PayPal representative will reach out to you once we evaluate your
            business needs.
          </p>
          <div data-testid="form-submit-success" className={styles.container}>
            <div className={styles.header}>
              <div className={styles.imageWrapper}>
                <img
                  src="https://www.paypalobjects.com/devdoc/multipartyimages/accept-payments.png"
                  alt="accept-payments"
                />
              </div>
              <h2>Advanced checkout solution</h2>
            </div>
            <p>
              Download the packaged solution and review the information. A
              PayPal representative will reach out to you soon with next steps.
            </p>
            <p>Files included in the package:</p>
            <ol>
              <li>Solution overview</li>
              <li>Integration guide</li>
              <li>Integration walkthrough</li>
              <li>FAQ</li>
            </ol>
            <Button className={styles.btnColor} onClick={downloadZip}>
              Download Zip file
            </Button>
          </div>
        </div>
      )}

      {formSubmitted && !cav_lt_10m && (
        <div className={styles.block}>
          <h2>Thank you</h2>
          <p className={styles.successMessage}>
            <span className={styles.greenTickSpace}>
              <GreenTick />
            </span>
            We are reviewing your request. A PayPal representative will contact
            you as soon as possible.
          </p>
        </div>
      )}

      {!formSubmitted && (
        <>
          <h1>Tell us about your platform</h1>
          <p>
            Complete this form, and a PayPal representative will contact you
            after they evaluate your business needs.
          </p>
          <ContextualAlert type="info" className={styles.mpInfo}>
            <span className={styles.mpInfoContent}>
              If you are a{' '}
              <span className={styles.mpBoldText}>
                seller or small business proprietor
              </span>{' '}
              interested in PayPal solutions, do not complete this form.
              Instead, visit{' '}
              <a
                href="https://www.paypal.com/us/business"
                className={`${styles.mpLink} ${styles.mpBoldText}`}
              >
                PayPal for Business
              </a>{' '}
              to learn more about the latest features and benefits of a PayPal
              Business Account.{' '}
            </span>
          </ContextualAlert>

          {!auto && (
            <form
              method="POST"
              action={pardotFormurl}
              onSubmit={(e: SyntheticEvent) =>
                handleFormSubmit(pardotFormName, e)
              }
              id={pardotFormName}
              data-pardot-form-name={pardotFormName}
            >
              <div className="ppvx_container--form">
                <div className={`ppvx_col-form--half ${styles.mppl0}`}>
                  <TextInput
                    name="firstName"
                    label={formContent.content.firstname_label}
                    value={formData.firstName}
                    onInput={(e) => {
                      onFormChange(e.target.name, e.target.value)
                    }}
                    helperText="​"
                    required
                    errorText={
                      formErrors.firstName.isTouched &&
                      !formErrors.firstName.isValid
                        ? formContent.content.firstname_error
                        : ''
                    }
                  />

                  <TextInput
                    name="lastName"
                    label={formContent.content.lastname_label}
                    value={formData.lastName}
                    onInput={(e) => {
                      onFormChange(e.target.name, e.target.value)
                    }}
                    helperText="​"
                    required
                    errorText={
                      formErrors.lastName.isTouched &&
                      !formErrors.lastName.isValid
                        ? formContent.content.lastname_error
                        : ''
                    }
                  />

                  <TextInput
                    name="emailAddress"
                    label={formContent.content.emailaddress_label}
                    value={formData.emailAddress}
                    onInput={(e) => {
                      onFormChange(e.target.name, e.target.value)
                    }}
                    helperText="​"
                    required
                    errorText={
                      formErrors.emailAddress.isTouched &&
                      !formErrors.emailAddress.isValid
                        ? formContent.content.emailaddress_error
                        : ''
                    }
                  />

                  <TextInput
                    name="company"
                    label={formContent.content.company_label}
                    value={formData.company}
                    onInput={(e) => {
                      onFormChange(e.target.name, e.target.value)
                    }}
                    helperText="​"
                    required
                    errorText={
                      formErrors.company.isTouched &&
                      !formErrors.company.isValid
                        ? formContent.content.company_error
                        : ''
                    }
                  />
                  <TextInput
                    name="companyWebsite"
                    label={formContent.content.website_label}
                    value={formData.companyWebsite}
                    onInput={(e) => {
                      onFormChange(e.target.name, e.target.value)
                    }}
                    helperText="​"
                    required
                    errorText={
                      formErrors.companyWebsite.isTouched &&
                      !formErrors.companyWebsite.isValid
                        ? formContent.content.website_error
                        : ''
                    }
                  />

                  <DropdownMenu
                    name="country"
                    label={formContent.content.country_label}
                    value={formData.country}
                    options={countries}
                    onChange={(e) => {
                      onFormChange('country', e.target.value)
                    }}
                    helperText="​"
                    errorText={
                      formErrors.country.isTouched &&
                      !formErrors.country.isValid
                        ? formContent.content.country_error
                        : ''
                    }
                  />
                  <TextArea
                    name="describeYourSolution"
                    className={styles.mpText}
                    label={formContent.content.description_label}
                    value={formData.describeYourSolution}
                    required
                    onChange={(e) =>
                      onFormChange(e.target.name, e.target.value)
                    }
                    errorText={
                      formErrors.describeYourSolution.isTouched &&
                      !formErrors.describeYourSolution.isValid
                        ? formContent.content.description_error
                        : ''
                    }
                  />
                  <DropdownMenu
                    name="companyType"
                    label={formContent.content.companyType_label}
                    value={formData.companyType}
                    options={[
                      {
                        value: 'Platform',
                        primaryText: 'Platform',
                      },
                      {
                        value: 'Marketplace',
                        primaryText: 'Marketplace',
                      },
                      {
                        value: 'Crowdfunding',
                        primaryText: 'Crowdfunding',
                      },
                      {
                        value: 'Sharing/Gig Economy',
                        primaryText: 'Sharing/Gig Economy',
                      },
                      {
                        value: 'Systems Integrator',
                        primaryText: 'Systems Integrator',
                      },
                      {
                        value: 'Developer',
                        primaryText: 'Developer',
                      },
                      {
                        value: 'Direct Seller',
                        primaryText: 'Direct Seller',
                      },
                    ]}
                    onChange={(e) => {
                      onFormChange('companyType', e.target.value)
                    }}
                    helperText="​"
                    errorText={
                      formErrors.companyType.isTouched &&
                      !formErrors.companyType.isValid
                        ? formContent.content.companyType_error
                        : ''
                    }
                  />
                  <DropdownMenu
                    name="clientOnlineVolume"
                    label={formContent.content.clientAnnual_label}
                    value={formData.clientOnlineVolume}
                    options={[
                      {
                        value: lessThanTargetVl,
                        primaryText: 'Less than $10 Million',
                      },
                      {
                        value: greaterThanTargetVl,
                        primaryText: 'More than $10 Million',
                      },
                    ]}
                    onChange={(e) => {
                      onFormChange('clientOnlineVolume', e.target.value)
                    }}
                    helperText="​"
                    errorText={
                      formErrors.clientOnlineVolume.isTouched &&
                      !formErrors.clientOnlineVolume.isValid
                        ? formContent.content.clientAnnual_error
                        : ''
                    }
                  />
                </div>

                {formData.clientOnlineVolume === lessThanTargetVl ? (
                  <div className={`ppvx_col-form--half ${styles.mppl0}`}>
                    <RadioButton
                      name="productsandServices"
                      label="Are your sellers the one who are going to receive the Payments for products/services?"
                      value={formData.productsandServices}
                      options={[
                        { label: 'Yes', value: 'yes' },
                        { label: 'No', value: 'no' },
                      ]}
                      onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                        onFormChange(e.target.name, e.target.value)
                      }
                    />
                    <RadioButton
                      name="cardProcessing"
                      label="Do you have card processing relationships in place?"
                      value={formData.cardProcessing}
                      className={styles.mpmt}
                      options={[
                        { label: 'Yes', value: 'yes' },
                        { label: 'No', value: 'no' },
                      ]}
                      onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                        onFormChange(e.target.name, e.target.value)
                      }
                    />
                    {formData.cardProcessing === 'yes' && (
                      <div className={styles.mpmt}>
                        <div className={styles.paymentProcessorLabel}>
                          Who are you currently processing with?
                        </div>
                        <TextInput
                          name="currentlyProcessing"
                          label={formContent.content.payment_processor}
                          value={formData.currentlyProcessing}
                          onChange={(e) => {
                            onFormChange(e.target.name, e.target.value)
                          }}
                          helperText="​"
                          required
                          errorText={
                            formErrors.currentlyProcessing.isTouched &&
                            !formErrors.currentlyProcessing.isValid
                              ? formContent.content.payment_processor_error
                              : ''
                          }
                        />
                      </div>
                    )}
                  </div>
                ) : null}

                <div className="ppvx_col-form--half">
                  <div className={styles.privacyTermContainer}>
                    <div className={styles.privacyTerm}>
                      <span>
                        <input
                          type="checkbox"
                          name="privacyConsent"
                          checked={formData.privacyConsent}
                          style={{
                            width: '1.5rem',
                            height: '1.5rem',
                            borderRadius: '10px',
                          }}
                          onChange={(
                            e: React.ChangeEvent<HTMLInputElement>,
                          ) => {
                            onFormChange(e.target.name, e.target.checked)
                          }}
                          required
                        />
                      </span>
                      <span>
                        I have read and acknowledged the{' '}
                        <a
                          href="https://www.paypal.com/webapps/mpp/ua/privacy-full"
                          className="terms_and_conditions_link dx-external-href"
                          target="_blank"
                          rel="noreferrer"
                        >
                          Privacy Policy
                        </a>{' '}
                        and agree that PayPal may contact me at the email above
                        to obtain more information about my submission.
                      </span>
                    </div>
                    {formErrors.privacyConsent.isTouched &&
                      !formErrors.privacyConsent.isValid && (
                        <div className={styles.privacyTermError}>
                          <span>
                            <svg
                              xmlns="http://www.w3.org/2000/svg"
                              fill="#C50A0C"
                              viewBox="0 0 24 24"
                              width="1.65rem"
                              height="1.65rem"
                              data-ppui="true"
                            >
                              <path
                                fillRule="evenodd"
                                d="M11.105 3.554a1 1 0 0 1 1.79 0l7.498 14.998a1 1 0 0 1-.895 1.447H4.502a1 1 0 0 1-.895-1.447l7.498-14.998zm.384 10.96h1.02c.168 0 .276-.096.288-.264.018-.428.042-.85.067-1.28.055-.98.113-2.002.113-3.244v-.948c0-.18-.096-.276-.276-.276h-1.404c-.18 0-.277.096-.277.276v.948c0 1.242.058 2.264.114 3.243.024.43.048.853.066 1.281.013.168.12.264.289.264zm.51 3.486A1 1 0 1 0 12 16a1 1 0 0 0 0 2z"
                                clipRule="evenodd"
                                data-ppui="true"
                              />
                            </svg>
                          </span>
                          <span>
                            Please accept terms and conditions to continue
                            signing up.
                          </span>
                        </div>
                      )}
                  </div>
                </div>

                <div className="ppvx_row">
                  <div className="ppvx_col-form--full signup-form">
                    <Button type="submit" style={{ width: '424px' }}>
                      {formContent.content.signupbutton_label}
                    </Button>
                  </div>
                </div>
              </div>
            </form>
          )}
        </>
      )}
    </div>
  )
}

export default withAnalytics(MultipartyForm)
